import { useTranslation } from 'react-i18next'
import { Dot } from '@/components/foundations/dot-icon'
import { cn } from '@/lib/utils'
import type { OutcomeType } from '@/types/conversation'

const GRAY = {
  badge:
    'bg-[#f5f4f3] text-[#424240] ring-[#d6d3d2] dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-600',
  dot: 'text-[#797976] dark:text-gray-400',
}

// Sampled from the conversations badge design. The shared badge palette cannot
// express these fills, so the pill is built to the same size as BadgeWithDot.
const outcomeColors: Record<OutcomeType, { badge: string; dot: string }> = {
  completed: {
    badge:
      'bg-[#effdf4] text-[#33744b] ring-[#baedc9] dark:bg-green-950 dark:text-green-300 dark:ring-green-800',
    dot: 'text-[#53b070] dark:text-green-400',
  },
  business_follow_up: {
    badge:
      'bg-[#f0f7e8] text-[#394e1d] ring-[#ddeace] dark:bg-lime-950 dark:text-lime-200 dark:ring-lime-800',
    dot: 'text-[#648041] dark:text-lime-400',
  },
  caller_follow_up: {
    badge:
      'bg-[#f5f4fe] text-[#605294] ring-[#e9e6fd] dark:bg-purple-950 dark:text-purple-300 dark:ring-purple-800',
    dot: 'text-[#8d7dd5] dark:text-purple-400',
  },
  transferred: {
    badge:
      'bg-[#f1ebe7] text-[#5a3a2c] ring-[#e2c4b7] dark:bg-stone-900 dark:text-stone-300 dark:ring-stone-700',
    dot: 'text-[#936854] dark:text-stone-400',
  },
  information_provided: {
    badge:
      'bg-[#f0f7fe] text-[#356196] ring-[#c1dcfc] dark:bg-blue-950 dark:text-blue-300 dark:ring-blue-800',
    dot: 'text-[#427ab9] dark:text-blue-400',
  },
  no_action: {
    badge:
      'bg-white text-[#424240] ring-[#d6d3d2] dark:bg-gray-900 dark:text-gray-300 dark:ring-gray-600',
    dot: 'text-[#797976] dark:text-gray-400',
  },
  answered_by_business: GRAY,
  missed_by_business: GRAY,
}

export function InboxOutcomeBadge({ outcome }: { outcome: OutcomeType }) {
  const { t } = useTranslation()
  const colors = outcomeColors[outcome] ?? GRAY

  return (
    <span
      className={cn(
        'inline-flex size-max items-center gap-1 rounded-full py-0.5 pr-2 pl-1.5 text-xs font-medium whitespace-nowrap ring-1 ring-inset',
        colors.badge
      )}
    >
      <Dot size="sm" className={colors.dot} />
      {t(`resources.conversations.outcomes.${outcome}`, {
        defaultValue: t('resources.conversations.outcomes.none'),
      })}
    </span>
  )
}
